import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Logo } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TKEY = "etprime_admin_token";

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString("en-AU", { timeZone: "Australia/Sydney" }); }
  catch { return iso; }
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TKEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [confs, setConfs] = useState([]);
  const [tab, setTab] = useState("enquiries");

  const load = useCallback(async (t) => {
    try {
      const cfg = { headers: { Authorization: `Bearer ${t}` } };
      const [e, c] = await Promise.all([
        axios.get(`${API}/admin/enquiries`, cfg),
        axios.get(`${API}/admin/wholesale-confirmations`, cfg),
      ]);
      setEnquiries(e.data); setConfs(c.data);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem(TKEY); setToken(""); }
    }
  }, []);

  useEffect(() => { if (token) load(token); }, [token, load]);

  const login = async (ev) => {
    ev.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TKEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed.");
    } finally { setBusy(false); }
  };

  const logout = () => { localStorage.removeItem(TKEY); setToken(""); setEnquiries([]); setConfs([]); };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-sm border border-white/10 bg-[#11151C]/60 p-10">
          <Logo />
          <h1 className="font-display text-2xl text-white mt-8">Admin sign in</h1>
          <p className="text-sm text-[#737882] mt-2">Confirmations &amp; enquiries — internal use.</p>
          <div className="space-y-2 mt-8">
            <Label className="text-xs uppercase tracking-widest text-[#737882]">Email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email"
              className="bg-transparent border-white/15 rounded-none h-12 text-white focus-visible:ring-0 focus-visible:border-[#C8A45A]" />
          </div>
          <div className="space-y-2 mt-5">
            <Label className="text-xs uppercase tracking-widest text-[#737882]">Password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password"
              className="bg-transparent border-white/15 rounded-none h-12 text-white focus-visible:ring-0 focus-visible:border-[#C8A45A]" />
          </div>
          {error && <p className="text-sm text-red-400 mt-4" data-testid="admin-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="admin-login-btn"
            className="w-full mt-8 bg-[#C8A45A] text-[#0A0D12] hover:bg-[#D4B572] transition-colors px-6 py-4 text-sm font-medium tracking-wide disabled:opacity-60">
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  const tabs = [
    { id: "enquiries", label: `Enquiries (${enquiries.length})` },
    { id: "confs", label: `Wholesale confirmations (${confs.length})` },
  ];

  return (
    <div className="min-h-screen max-w-7xl mx-auto px-6 md:px-12 py-28" data-testid="admin-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <span className="overline">Internal · Records</span>
          <h1 className="font-display text-3xl md:text-4xl font-light text-white mt-3">Confirmations &amp; Enquiries</h1>
        </div>
        <button onClick={logout} data-testid="admin-logout"
          className="border border-white/20 text-[#A1A5AB] hover:text-white hover:border-white/40 transition-colors px-5 py-2.5 text-sm">
          Sign out
        </button>
      </div>

      <div className="flex gap-2 mt-10 border-b border-white/10">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
            className={`px-5 py-3 text-sm border-b-2 -mb-px transition-colors ${tab === t.id ? "border-[#C8A45A] text-white" : "border-transparent text-[#737882] hover:text-white"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8 overflow-x-auto">
        {tab === "enquiries" ? (
          <table className="w-full text-sm border border-white/10" data-testid="enquiries-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-[#737882] bg-[#11151C]">
                {["Received (AEST)", "Name", "Email", "Firm", "Client type", "Wholesale", "Message"].map((h) => (
                  <th key={h} className="px-4 py-3 font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {enquiries.map((r, i) => (
                <tr key={i} className="border-t border-white/10 text-[#A1A5AB] align-top">
                  <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">{fmt(r.created_at)}</td>
                  <td className="px-4 py-3 text-white">{r.name}</td>
                  <td className="px-4 py-3">{r.email}</td>
                  <td className="px-4 py-3">{r.company}</td>
                  <td className="px-4 py-3">{r.clientType || "—"}</td>
                  <td className="px-4 py-3">{r.wholesaleConfirmed ? <span className="gold-text">Confirmed</span> : "No"}</td>
                  <td className="px-4 py-3 max-w-xs">{r.message || "—"}</td>
                </tr>
              ))}
              {!enquiries.length && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#737882]">No enquiries yet.</td></tr>}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-sm border border-white/10" data-testid="confs-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-[#737882] bg-[#11151C]">
                {["Timestamp (AEST)", "Confirmed", "IP address", "Page", "User agent"].map((h) => (
                  <th key={h} className="px-4 py-3 font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {confs.map((r, i) => (
                <tr key={i} className="border-t border-white/10 text-[#A1A5AB] align-top">
                  <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">{fmt(r.created_at)}</td>
                  <td className="px-4 py-3">{r.confirmed ? <span className="gold-text">Yes</span> : "No"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.ip || "—"}</td>
                  <td className="px-4 py-3">{r.path || "—"}</td>
                  <td className="px-4 py-3 max-w-md text-xs text-[#737882]">{r.userAgent || "—"}</td>
                </tr>
              ))}
              {!confs.length && <tr><td colSpan={5} className="px-4 py-8 text-center text-[#737882]">No confirmations yet.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
